import { Loader2, RefreshCw, TriangleAlert } from 'lucide-react'
import { cn } from '@/lib/utils'
import { dataSourceState } from '@/lib/deep-dive-adapters'
import { formatTimestamp } from '@/lib/format'
import type { Freshness } from '@/lib/system-intelligence'

/**
 * What a panel shows instead of its chart or table.
 *
 * The deep-dive endpoints have four distinct ways of not answering — the source
 * was never connected (503), the upstream was slow (504), it answered with
 * something unusable (502), or we sent a bad parameter (400) — and the contract
 * gives each its own wording. Rendering them all as "something went wrong" would
 * throw away the only thing that tells an on-call engineer whether to retry or
 * to go and check a deployment, so the mapping in `dataSourceState` is honoured
 * here and the retry button appears only where retrying can help.
 */
export function PanelError({
  error,
  onRetry,
  className,
}: {
  error: unknown
  onRetry?: () => void
  className?: string
}) {
  const state = dataSourceState(error)

  return (
    <div
      role="status"
      className={cn('flex flex-col items-center justify-center gap-2 px-5 py-10 text-center', className)}
    >
      <TriangleAlert aria-hidden className="text-degraded size-5" />
      <p className="text-fg font-mono text-[13px] font-semibold">{state.headline}</p>
      {state.detail && <p className="text-fg-subtle max-w-md text-[13px]">{state.detail}</p>}
      {state.retryable && onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="border-line-bright text-fg-muted hover:bg-surface-3 hover:text-fg focus-visible:ring-primary/30 mt-1 flex items-center gap-2 rounded-lg border px-3 py-1.5 font-mono text-[11px] font-semibold tracking-[0.08em] uppercase transition-colors outline-none focus-visible:ring-2"
        >
          <RefreshCw className="size-3.5" />
          Retry
        </button>
      )}
    </div>
  )
}

/** The waiting state, sized to sit in the same hole the content will fill. */
export function PanelLoading({ label = 'Loading', className }: { label?: string; className?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn('text-fg-subtle flex items-center justify-center gap-2 px-5 py-10', className)}
    >
      <Loader2 aria-hidden className="size-4 animate-spin" />
      <span className="font-mono text-[11px] font-semibold tracking-[0.08em] uppercase">
        {label}…
      </span>
    </div>
  )
}

/** A panel that fetched fine and simply has nothing in it. */
export function PanelEmpty({ label, className }: { label: string; className?: string }) {
  return (
    <p className={cn('text-fg-subtle px-5 py-10 text-center text-[13px]', className)}>{label}</p>
  )
}

/**
 * The freshness line under an OWEHub-backed panel.
 *
 * `sync_healthy: false` is the case worth interrupting for: the numbers still
 * render, they are simply not necessarily current, and nothing else on the
 * screen would say so. The as-of stamp is quiet by comparison — it earns a line
 * of small text, not a badge.
 */
export function FreshnessNote({ freshness, className }: { freshness?: Freshness; className?: string }) {
  if (!freshness) return null
  const { dataAsOf, syncHealthy } = freshness

  return (
    <p
      className={cn(
        'mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] tracking-[0.06em] uppercase',
        className,
      )}
    >
      {!syncHealthy && (
        <span
          title="The upstream sync is failing — these figures may be out of date."
          className="border-degraded/50 text-degraded rounded border px-1.5 py-0.5 font-semibold"
        >
          Stale
        </span>
      )}
      <span className="text-fg-subtle">
        {dataAsOf ? `Data as of ${formatTimestamp(dataAsOf)}` : 'No matching data'}
      </span>
    </p>
  )
}
